import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { ENDPOINTS } from '../config/api';

// Types
export type PoolNetwork = 'ETHEREUM' | 'BSC' | 'SOLANA';
export type PoolProvider = 'uniswap' | 'pancakeswap' | 'jupiter' | 'raydium';
export type RiskLevel = 'LOW' | 'MEDIUM' | 'HIGH';

export interface DefiPool {
  id: string;
  name: string;
  network: PoolNetwork;
  provider: PoolProvider;
  tokens: string[];
  apy: number;
  tvl: number;
  riskLevel: RiskLevel;
  description?: string;
  minDeposit?: number;
  fees?: {
    deposit: number;
    withdrawal: number;
    performance: number;
  };
}

export interface PoolPosition {
  id: string;
  poolId: string;
  pool: DefiPool;
  depositedAmount: number;
  depositedAmountBrl: number;
  currentValue: number;
  currentValueBrl: number;
  earnings: number;
  earningsPercentage: number;
  depositedAt: string;
  status: 'ACTIVE' | 'WITHDRAWING' | 'WITHDRAWN';
  txHash?: string;
}

export interface PoolFilters {
  network?: PoolNetwork;
  provider?: PoolProvider;
  riskLevel?: RiskLevel;
}

export interface DepositRequest {
  poolId: string;
  amountBrl: number;
  agentId?: string;
}

export interface WithdrawRequest {
  positionId: string;
  agentId?: string;
}

// ==================== POOLS ====================

export function useDefiPools(filters?: PoolFilters) {
  return useQuery<DefiPool[]>({
    queryKey: ['defi', 'pools', filters],
    queryFn: async () => {
      try {
        const params = new URLSearchParams();
        if (filters?.network) params.append('network', filters.network);
        if (filters?.provider) params.append('provider', filters.provider);
        if (filters?.riskLevel) params.append('riskLevel', filters.riskLevel);
        
        const response = await api.get(`${ENDPOINTS.defiPools.list}?${params}`);
        const data = response.data;
        
        // Handle different response formats
        if (Array.isArray(data)) return data;
        if (data?.pools && Array.isArray(data.pools)) return data.pools;
        return [];
      } catch (error) {
        console.warn('Failed to fetch DeFi pools:', error);
        return [];
      }
    },
  });
}

export function useDefiPool(poolId: string) {
  return useQuery<DefiPool>({
    queryKey: ['defi', 'pool', poolId],
    queryFn: async () => {
      const response = await api.get(ENDPOINTS.defiPools.get(poolId));
      return response.data;
    },
    enabled: !!poolId,
  });
}

// ==================== POSITIONS ====================

export function useDefiPositions(virtualAccountId: string, filters?: { status?: string; provider?: string }) {
  return useQuery<PoolPosition[]>({
    queryKey: ['defi', 'positions', virtualAccountId, filters],
    queryFn: async () => {
      if (!virtualAccountId) return [];
      
      try {
        // Use the virtual account crypto positions endpoint
        const params = new URLSearchParams();
        if (filters?.status) params.append('status', filters.status);
        if (filters?.provider) params.append('provider', filters.provider);
        
        const response = await api.get(`${ENDPOINTS.crypto.positions(virtualAccountId)}?${params}`);
        const data = response.data;
        
        // Handle different response formats
        if (Array.isArray(data)) return data;
        if (data?.positions && Array.isArray(data.positions)) return data.positions;
        return [];
      } catch (error) {
        console.warn('Failed to fetch DeFi positions:', error);
        return [];
      }
    },
    enabled: !!virtualAccountId,
  });
}

export function useDefiPosition(virtualAccountId: string, positionId: string) {
  return useQuery<PoolPosition>({
    queryKey: ['defi', 'position', virtualAccountId, positionId],
    queryFn: async () => {
      const response = await api.get(ENDPOINTS.crypto.positionById(virtualAccountId, positionId));
      return response.data;
    },
    enabled: !!virtualAccountId && !!positionId,
  });
}

// ==================== DEPOSIT & WITHDRAW ====================

export function usePoolDeposit(virtualAccountId: string) {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data: DepositRequest) => {
      const response = await api.post(ENDPOINTS.defiPools.deposit(data.poolId), {
        virtualAccountId,
        amountBrl: Math.round(data.amountBrl * 100), // Convert to centavos
        agentId: data.agentId,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['defi', 'positions', virtualAccountId] });
      queryClient.invalidateQueries({ queryKey: ['banking', 'balance'] });
      queryClient.invalidateQueries({ queryKey: ['virtual-account', 'balance'] });
    },
  });
}

export function usePoolWithdraw(virtualAccountId: string) {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data: WithdrawRequest) => {
      const response = await api.post(ENDPOINTS.defiPools.withdraw(data.positionId), {
        virtualAccountId,
        agentId: data.agentId,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['defi', 'positions', virtualAccountId] });
      queryClient.invalidateQueries({ queryKey: ['banking', 'balance'] });
      queryClient.invalidateQueries({ queryKey: ['crypto', 'wallets', virtualAccountId] });
    },
  });
}

// ==================== PORTFOLIO SUMMARY ====================

export function useDefiPortfolio(virtualAccountId: string) {
  const { data: positions = [], isLoading } = useDefiPositions(virtualAccountId, { status: 'ACTIVE' });
  
  const portfolio = {
    totalDeposited: 0,
    totalDepositedBrl: 0,
    currentValue: 0,
    currentValueBrl: 0,
    totalEarnings: 0,
    totalEarningsPercentage: 0,
    positionsCount: positions.length,
  };
  
  if (positions.length > 0) {
    positions.forEach(position => {
      portfolio.totalDeposited += position.depositedAmount;
      portfolio.totalDepositedBrl += position.depositedAmountBrl;
      portfolio.currentValue += position.currentValue;
      portfolio.currentValueBrl += position.currentValueBrl;
      portfolio.totalEarnings += position.earnings;
    });
    
    if (portfolio.totalDeposited > 0) {
      portfolio.totalEarningsPercentage = (portfolio.totalEarnings / portfolio.totalDeposited) * 100;
    }
  }
  
  return {
    portfolio,
    positions,
    isLoading,
  };
}

// ==================== RISK HELPERS ====================

export function getRiskColor(level: RiskLevel): string {
  switch (level) {
    case 'LOW':
      return 'text-green-400 bg-green-500/20';
    case 'MEDIUM':
      return 'text-yellow-400 bg-yellow-500/20';
    case 'HIGH':
      return 'text-red-400 bg-red-500/20';
    default:
      return 'text-surface-400 bg-surface-500/20';
  }
}

export function getRiskLabel(level: RiskLevel): string {
  switch (level) {
    case 'LOW':
      return 'Baixo Risco';
    case 'MEDIUM':
      return 'Risco Moderado';
    case 'HIGH':
      return 'Alto Risco';
    default:
      return level;
  }
}

export function getNetworkColor(network: PoolNetwork): string {
  switch (network) {
    case 'ETHEREUM':
      return 'from-blue-500 to-purple-500';
    case 'BSC':
      return 'from-yellow-500 to-orange-500';
    case 'SOLANA':
      return 'from-purple-500 to-pink-500';
    default:
      return 'from-surface-500 to-surface-600';
  }
}

export function getProviderLogo(provider: PoolProvider): string {
  // In a real app, return actual logo URLs
  switch (provider) {
    case 'uniswap':
      return '🦄';
    case 'pancakeswap':
      return '🥞';
    case 'jupiter':
      return '🪐';
    case 'raydium':
      return '☀️';
    default:
      return '💱';
  }
}
